import React, { useEffect, useState, useMemo } from "react";
import { useMsal } from "@azure/msal-react";
import DataTable, { TableColumn } from "react-data-table-component";
import {
    BarChart3,
    Users,
    Clock,
    Target,
    TrendingUp,
    Download,
    Search,
    Filter,
    DollarSign,
    Calendar,
} from "lucide-react";
import * as XLSX from "xlsx";
import { getApiBaseUrl } from "../utils/baseUrl";
import { useLoading, acquireTokenSafe } from "../App";

// ── Interfaces ──────────────────────────────────────────────

interface StaffMember {
    ID: number;
    Code: string;
    Name: string;
    FirstName: string;
    LastName: string;
    Email1: string | null;
    Grade: string;
    Status: string;
    StaffType: string;
    TakeOnDate: string | null;
    LeavingDate: string | null;
    AccUnits: number;
    BudgetCh: number;
    BudgetNonCh: number;
}

interface WipByStaff {
    StaffID: number;
    EntryCount: number;
    TotalUnits: number;
    TotalChargeable: number;
    TotalNonChargeable: number;
    TotalHours: number;
    TotalRevenue: number;
    TotalGross: number;
    TotalCost: number;
    FirstEntry: string;
    LastEntry: string;
    UniqueClients: number;
    UniqueJobs: number;
}

interface WipMonthly {
    StaffID: number;
    Month: string;
    Chargeable: number;
    NonChargeable: number;
    TotalHours: number;
    Revenue: number;
    Entries: number;
    Clients: number;
}

interface WipByCategory {
    StaffID: number;
    Category: string;
    Chargeable: number;
    NonChargeable: number;
    TotalHours: number;
    Revenue: number;
    Clients: number;
}

interface StaffUtilisation {
    id: number;
    code: string;
    name: string;
    grade: string;
    status: string;
    staffType: string;
    accUnits: number;
    entryCount: number;
    totalUnits: number;
    chargeableHours: number;
    nonChargeableHours: number;
    totalHours: number;
    totalRevenue: number;
    utilisationRate: number;
    clients: number;
    jobs: number;
    firstEntry: string | null;
    lastEntry: string | null;
    revenuePerHour: number;
    monthlyData: WipMonthly[];
    categoryData: WipByCategory[];
}

// ── Helpers ─────────────────────────────────────────────────

const formatDateInput = (d: Date): string => d.toISOString().split("T")[0];

type DatePreset = "week" | "30days" | "ytd" | "custom";

const getDatesForPreset = (preset: DatePreset): { start: string; end: string } => {
    const now = new Date();
    const end = formatDateInput(now);
    switch (preset) {
        case "week": {
            const day = now.getDay();
            const diff = now.getDate() - day + (day === 0 ? -6 : 1);
            return { start: formatDateInput(new Date(now.getFullYear(), now.getMonth(), diff)), end };
        }
        case "30days": {
            const d = new Date(now);
            d.setDate(d.getDate() - 30);
            return { start: formatDateInput(d), end };
        }
        case "ytd":
            return { start: formatDateInput(new Date(now.getFullYear(), 0, 1)), end };
        default:
            return { start: "", end: "" };
    }
};

const formatCurrency = (val: number | null | undefined): string => {
    if (val === null || val === undefined) return "—";
    return `€${val.toLocaleString("en-IE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const formatDate = (val: string | null | undefined): string => {
    if (!val) return "—";
    try {
        const d = new Date(val);
        if (isNaN(d.getTime())) return "—";
        return d.toLocaleDateString("en-IE");
    } catch {
        return "—";
    }
};

const utilisationColor = (rate: number): string => {
    if (rate >= 70) return "var(--status-success)";
    if (rate >= 50) return "var(--status-warning, #f0ad4e)";
    return "var(--status-error, #d9534f)";
};

// ── Custom table styles (matches Master Services List) ──────

const customStyles = {
    table: {
        style: {
            backgroundColor: "var(--bg-secondary)",
            borderRadius: "8px",
        },
    },
    headRow: {
        style: {
            backgroundColor: "var(--bg-tertiary)",
            borderBottomColor: "var(--border-color)",
            color: "var(--text-primary)",
            fontWeight: 600,
            fontSize: "0.85rem",
        },
    },
    headCells: {
        style: {
            color: "var(--text-secondary)",
            paddingLeft: "16px",
            paddingRight: "16px",
        },
    },
    rows: {
        style: {
            backgroundColor: "var(--bg-secondary)",
            color: "var(--text-primary)",
            borderBottomColor: "var(--border-color)",
        },
        highlightOnHoverStyle: {
            backgroundColor: "var(--bg-hover)",
            color: "var(--text-primary)",
            borderBottomColor: "var(--border-color)",
        },
    },
    cells: {
        style: {
            color: "var(--text-primary)",
            paddingLeft: "16px",
            paddingRight: "16px",
        },
    },
    pagination: {
        style: {
            backgroundColor: "var(--bg-secondary)",
            color: "var(--text-secondary)",
            borderTopColor: "var(--border-color)",
        },
        pageButtonsStyle: {
            color: "var(--text-secondary)",
            fill: "var(--text-secondary)",
            "&:hover:not(:disabled)": {
                backgroundColor: "var(--bg-hover)",
            },
            "&:disabled": {
                opacity: 0.4,
            },
        },
    },
    expanderRow: {
        style: {
            backgroundColor: "var(--bg-secondary)",
            color: "var(--text-primary)",
        },
    },
};

// ── Expandable Row Sub-Component ────────────────────────────

interface ExpandProps {
    data: StaffUtilisation;
}

const ExpandedStaffDetail: React.FC<ExpandProps> = ({ data }) => {
    const recentMonths = data.monthlyData.slice(0, 12);
    const sortedCategories = [...data.categoryData].sort((a, b) => b.TotalHours - a.TotalHours);

    return (
        <div style={{ padding: "12px 24px" }}>
            {/* Monthly Breakdown */}
            {recentMonths.length > 0 && (
                <>
                    <h4 style={{ margin: "0 0 12px", color: "var(--text-primary)", fontSize: 14, fontWeight: 600 }}>
                        <Clock size={16} style={{ verticalAlign: "middle", marginRight: 6 }} />
                        Monthly Breakdown — {data.name}
                    </h4>
                    <table className="data-table" style={{ width: "100%", marginBottom: 16 }}>
                        <thead>
                            <tr>
                                <th>Month</th>
                                <th className="text-right">Chargeable</th>
                                <th className="text-right">Non-Chargeable</th>
                                <th className="text-right">Total</th>
                                <th className="text-right">Revenue</th>
                                <th className="text-right">Clients</th>
                                <th className="text-right">Utilisation</th>
                            </tr>
                        </thead>
                        <tbody>
                            {recentMonths.map((m) => {
                                const utilRate = m.TotalHours > 0 ? (m.Chargeable / m.TotalHours) * 100 : 0;
                                return (
                                    <tr key={m.Month}>
                                        <td className="cell-primary">{m.Month}</td>
                                        <td className="text-right cell-value accent">{m.Chargeable.toFixed(1)}h</td>
                                        <td className="text-right cell-secondary">{m.NonChargeable.toFixed(1)}h</td>
                                        <td className="text-right cell-value">{m.TotalHours.toFixed(1)}h</td>
                                        <td className="text-right cell-secondary">{formatCurrency(m.Revenue)}</td>
                                        <td className="text-right cell-secondary">{m.Clients}</td>
                                        <td className="text-right" style={{ fontWeight: 600, color: utilisationColor(utilRate) }}>
                                            {utilRate.toFixed(1)}%
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </>
            )}

            {/* Category Breakdown */}
            {sortedCategories.length > 0 && (
                <>
                    <h4 style={{ margin: "0 0 12px", color: "var(--text-primary)", fontSize: 14, fontWeight: 600 }}>
                        <BarChart3 size={16} style={{ verticalAlign: "middle", marginRight: 6 }} />
                        Work by Category
                    </h4>
                    <table className="data-table" style={{ width: "100%" }}>
                        <thead>
                            <tr>
                                <th>Category</th>
                                <th className="text-right">Chargeable</th>
                                <th className="text-right">Non-Chargeable</th>
                                <th className="text-right">Total</th>
                                <th className="text-right">Revenue</th>
                                <th className="text-right">Clients</th>
                            </tr>
                        </thead>
                        <tbody>
                            {sortedCategories.map((c, i) => (
                                <tr key={`${c.Category}-${i}`}>
                                    <td className="cell-primary">{c.Category || "Uncategorised"}</td>
                                    <td className="text-right cell-value accent">{c.Chargeable.toFixed(1)}h</td>
                                    <td className="text-right cell-secondary">{c.NonChargeable.toFixed(1)}h</td>
                                    <td className="text-right cell-value">{c.TotalHours.toFixed(1)}h</td>
                                    <td className="text-right cell-secondary">{formatCurrency(c.Revenue)}</td>
                                    <td className="text-right cell-secondary">{c.Clients}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </>
            )}

            {recentMonths.length === 0 && sortedCategories.length === 0 && (
                <div style={{ padding: 16, textAlign: "center", color: "var(--text-muted)" }}>
                    No detailed data available for this staff member
                </div>
            )}
        </div>
    );
};

// ── Main Component ──────────────────────────────────────────

const CorkUtilisation: React.FC = () => {
    const { instance, accounts } = useMsal();
    const { setLoading } = useLoading();
    const [data, setData] = useState<StaffUtilisation[]>([]);
    const [error, setError] = useState<string | null>(null);
    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");
    const [gradeFilter, setGradeFilter] = useState("all");
    const [dateRange, setDateRange] = useState<DatePreset>("week");
    const [startDate, setStartDate] = useState(() => getDatesForPreset("week").start);
    const [endDate, setEndDate] = useState(() => getDatesForPreset("week").end);

    const handlePresetChange = (preset: DatePreset) => {
        setDateRange(preset);
        if (preset !== "custom") {
            const { start, end } = getDatesForPreset(preset);
            setStartDate(start);
            setEndDate(end);
        }
    };

    // ── Fetch data ───────────────────────────────────────────
    useEffect(() => {
        const fetchData = async () => {
            if (accounts.length === 0) return;
            setLoading(true);
            setError(null);
            try {
                const token = await acquireTokenSafe(instance, accounts);
                if (!token) { setLoading(false); return; }
                const params = new URLSearchParams();
                if (startDate) params.set("startDate", startDate);
                if (endDate) params.set("endDate", endDate);
                const qs = params.toString() ? `?${params.toString()}` : "";
                const response = await fetch(`${getApiBaseUrl()}/api/cork-utilisation${qs}`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                if (!response.ok) {
                    const errData = await response.json().catch(() => ({}));
                    throw new Error(errData.details || errData.error || `HTTP ${response.status}`);
                }
                const result = await response.json();

                const merged: StaffUtilisation[] = result.staff.map((s: StaffMember) => {
                    const wip: WipByStaff | undefined = result.wipByStaff.find((w: WipByStaff) => w.StaffID === s.ID);
                    const monthly: WipMonthly[] = result.wipMonthly
                        .filter((m: WipMonthly) => m.StaffID === s.ID)
                        .sort((a: WipMonthly, b: WipMonthly) => b.Month.localeCompare(a.Month));
                    const categories: WipByCategory[] = result.wipByCategory.filter(
                        (c: WipByCategory) => c.StaffID === s.ID
                    );

                    const chargeableHours = wip?.TotalChargeable || 0;
                    const totalHours = wip?.TotalHours || 0;
                    const utilisationRate = totalHours > 0 ? (chargeableHours / totalHours) * 100 : 0;
                    const revenuePerHour = chargeableHours > 0 ? (wip?.TotalRevenue || 0) / chargeableHours : 0;

                    return {
                        id: s.ID,
                        code: s.Code?.trim() || "",
                        name: s.Name?.trim() || "",
                        grade: s.Grade?.trim() || "",
                        status: s.Status?.trim() || "",
                        staffType: s.StaffType?.trim() || "",
                        accUnits: s.AccUnits || 0,
                        entryCount: wip?.EntryCount || 0,
                        totalUnits: wip?.TotalUnits || 0,
                        chargeableHours,
                        nonChargeableHours: wip?.TotalNonChargeable || 0,
                        totalHours,
                        totalRevenue: wip?.TotalRevenue || 0,
                        utilisationRate,
                        clients: wip?.UniqueClients || 0,
                        jobs: wip?.UniqueJobs || 0,
                        firstEntry: wip?.FirstEntry || null,
                        lastEntry: wip?.LastEntry || null,
                        revenuePerHour,
                        monthlyData: monthly,
                        categoryData: categories,
                    };
                });

                setData(merged);
            } catch (err: any) {
                console.error("Failed to fetch Cork utilisation:", err);
                setError(err.message || "Failed to fetch data");
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [accounts, instance, setLoading, startDate, endDate]);

    // ── Filters ──────────────────────────────────────────────
    const statuses = useMemo(() => [...new Set(data.map((d) => d.status).filter(Boolean))].sort(), [data]);
    const grades = useMemo(() => [...new Set(data.map((d) => d.grade).filter(Boolean))].sort(), [data]);

    const filteredData = useMemo(() => {
        return data.filter((d) => {
            if (statusFilter !== "all" && d.status !== statusFilter) return false;
            if (gradeFilter !== "all" && d.grade !== gradeFilter) return false;
            if (search) {
                const q = search.toLowerCase();
                return (
                    d.name.toLowerCase().includes(q) ||
                    d.code.toLowerCase().includes(q) ||
                    d.grade.toLowerCase().includes(q)
                );
            }
            return true;
        });
    }, [data, statusFilter, gradeFilter, search]);

    // ── Summary totals ───────────────────────────────────────
    const totals = useMemo(() => {
        const activeCount = filteredData.filter((d) => d.status === "Active").length;
        const t = filteredData.reduce(
            (acc, u) => ({
                headcount: acc.headcount + 1,
                totalHours: acc.totalHours + u.totalHours,
                chargeableHours: acc.chargeableHours + u.chargeableHours,
                nonChargeableHours: acc.nonChargeableHours + u.nonChargeableHours,
                totalRevenue: acc.totalRevenue + u.totalRevenue,
            }),
            { headcount: 0, totalHours: 0, chargeableHours: 0, nonChargeableHours: 0, totalRevenue: 0 }
        );
        return {
            ...t,
            activeCount,
            totalHours: Math.round(t.totalHours * 100) / 100,
            chargeableHours: Math.round(t.chargeableHours * 100) / 100,
            nonChargeableHours: Math.round(t.nonChargeableHours * 100) / 100,
            avgUtilisation:
                t.totalHours > 0
                    ? Math.round((t.chargeableHours / t.totalHours) * 10000) / 100
                    : 0,
        };
    }, [filteredData]);

    // ── Table columns ────────────────────────────────────────
    const columns: TableColumn<StaffUtilisation>[] = [
        {
            name: "Name",
            selector: (row) => row.name,
            sortable: true,
            grow: 2,
            cell: (row) => (
                <div>
                    <span className="cell-primary">{row.name}</span>
                    <div style={{ fontSize: 11, color: "var(--text-muted)" }}>{row.code}</div>
                </div>
            ),
        },
        {
            name: "Grade",
            selector: (row) => row.grade,
            sortable: true,
            cell: (row) => (
                <span className={`status-badge ${row.grade === "Partner" ? "active" : "inactive"}`}>
                    {row.grade}
                </span>
            ),
            width: "110px",
        },
        {
            name: "Status",
            selector: (row) => row.status,
            sortable: true,
            cell: (row) => (
                <span className={`status-badge ${row.status === "Active" ? "active" : "inactive"}`}>
                    {row.status}
                </span>
            ),
            width: "100px",
        },
        {
            name: "Chargeable",
            selector: (row) => row.chargeableHours,
            sortable: true,
            right: true,
            cell: (row) => (
                <span className="cell-value accent">{row.chargeableHours.toFixed(1)}h</span>
            ),
        },
        {
            name: "Non-Chargeable",
            selector: (row) => row.nonChargeableHours,
            sortable: true,
            right: true,
            cell: (row) => (
                <span className="cell-secondary">{row.nonChargeableHours.toFixed(1)}h</span>
            ),
        },
        {
            name: "Total Hours",
            selector: (row) => row.totalHours,
            sortable: true,
            right: true,
            cell: (row) => <span className="cell-value">{row.totalHours.toFixed(1)}h</span>,
        },
        {
            name: "Utilisation",
            selector: (row) => row.utilisationRate,
            sortable: true,
            right: true,
            cell: (row) => (
                <span
                    style={{
                        fontWeight: 600,
                        color: utilisationColor(row.utilisationRate),
                    }}
                >
                    {row.utilisationRate.toFixed(1)}%
                </span>
            ),
        },
        {
            name: "Revenue",
            selector: (row) => row.totalRevenue,
            sortable: true,
            right: true,
            cell: (row) => <span className="cell-value">{formatCurrency(row.totalRevenue)}</span>,
        },
        {
            name: "€/Hour",
            selector: (row) => row.revenuePerHour,
            sortable: true,
            right: true,
            cell: (row) => (
                <span className="cell-secondary">
                    {row.revenuePerHour > 0 ? formatCurrency(row.revenuePerHour) : "—"}
                </span>
            ),
            compact: true,
        },
        {
            name: "Clients",
            selector: (row) => row.clients,
            sortable: true,
            right: true,
            compact: true,
            cell: (row) => <span className="cell-secondary">{row.clients}</span>,
        },
        {
            name: "Entries",
            selector: (row) => row.entryCount,
            sortable: true,
            right: true,
            compact: true,
            cell: (row) => <span className="cell-secondary">{row.entryCount}</span>,
        },
    ];

    // ── Export CSV ────────────────────────────────────────────
    const downloadCSV = () => {
        if (filteredData.length === 0) return;
        const headers = [
            "Code", "Name", "Grade", "Status", "Target Hours/Week",
            "Chargeable Hours", "Non-Chargeable Hours", "Total Hours",
            "Utilisation Rate", "Revenue", "Revenue/Hour",
            "Clients", "Jobs", "First Entry", "Last Entry",
        ];
        const rows = filteredData.map((d) =>
            [
                d.code, `"${d.name}"`, d.grade, d.status, d.accUnits,
                d.chargeableHours.toFixed(1), d.nonChargeableHours.toFixed(1), d.totalHours.toFixed(1),
                d.utilisationRate.toFixed(1) + "%", d.totalRevenue.toFixed(2), d.revenuePerHour.toFixed(2),
                d.clients, d.jobs, formatDate(d.firstEntry), formatDate(d.lastEntry),
            ].join(",")
        );
        const csvContent = `${headers.join(",")}\n${rows.join("\n")}`;
        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = `cork-utilisation-${new Date().toISOString().split("T")[0]}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    // ── Export XLSX ──────────────────────────────────────────
    const downloadXLSX = () => {
        if (filteredData.length === 0) return;
        const wb = XLSX.utils.book_new();

        // Staff Summary sheet
        const summaryRows = filteredData.map((d) => ({
            Code: d.code,
            Name: d.name,
            Grade: d.grade,
            Status: d.status,
            "Target Hours/Week": d.accUnits,
            "Chargeable Hours": +d.chargeableHours.toFixed(1),
            "Non-Chargeable Hours": +d.nonChargeableHours.toFixed(1),
            "Total Hours": +d.totalHours.toFixed(1),
            "Utilisation %": +d.utilisationRate.toFixed(1),
            Revenue: +d.totalRevenue.toFixed(2),
            "Revenue/Hour": +d.revenuePerHour.toFixed(2),
            Clients: d.clients,
            Jobs: d.jobs,
            "First Entry": d.firstEntry ? formatDate(d.firstEntry) : "",
            "Last Entry": d.lastEntry ? formatDate(d.lastEntry) : "",
        }));
        const wsSummary = XLSX.utils.json_to_sheet(summaryRows);
        XLSX.utils.book_append_sheet(wb, wsSummary, "Staff Summary");

        // Monthly detail sheet
        const monthlyRows: any[] = [];
        filteredData.forEach((d) => {
            d.monthlyData.forEach((m) => {
                const utilRate = m.TotalHours > 0 ? (m.Chargeable / m.TotalHours) * 100 : 0;
                monthlyRows.push({
                    "Staff Code": d.code,
                    "Staff Name": d.name,
                    Month: m.Month,
                    "Chargeable Hours": +m.Chargeable.toFixed(1),
                    "Non-Chargeable Hours": +m.NonChargeable.toFixed(1),
                    "Total Hours": +m.TotalHours.toFixed(1),
                    Revenue: +m.Revenue,
                    "Utilisation %": +utilRate.toFixed(1),
                    Clients: m.Clients,
                    Entries: m.Entries,
                });
            });
        });
        if (monthlyRows.length > 0) {
            const wsMonthly = XLSX.utils.json_to_sheet(monthlyRows);
            XLSX.utils.book_append_sheet(wb, wsMonthly, "Monthly Detail");
        }

        // Category detail sheet
        const catRows: any[] = [];
        filteredData.forEach((d) => {
            d.categoryData.forEach((c) => {
                catRows.push({
                    "Staff Code": d.code,
                    "Staff Name": d.name,
                    Category: c.Category || "Uncategorised",
                    "Chargeable Hours": +c.Chargeable.toFixed(1),
                    "Non-Chargeable Hours": +c.NonChargeable.toFixed(1),
                    "Total Hours": +c.TotalHours.toFixed(1),
                    Revenue: +c.Revenue,
                    Clients: c.Clients,
                });
            });
        });
        if (catRows.length > 0) {
            const wsCat = XLSX.utils.json_to_sheet(catRows);
            XLSX.utils.book_append_sheet(wb, wsCat, "By Category");
        }

        XLSX.writeFile(wb, `cork-utilisation-${new Date().toISOString().split("T")[0]}.xlsx`);
    };

    // ── Preset button style helper ───────────────────────────
    const presetBtnStyle = (preset: DatePreset): React.CSSProperties => ({
        padding: "6px 14px",
        borderRadius: "6px",
        border: dateRange === preset ? "1px solid var(--accent-primary)" : "1px solid var(--border-color)",
        background: dateRange === preset ? "var(--accent-primary)" : "var(--bg-secondary)",
        color: dateRange === preset ? "#fff" : "var(--text-secondary)",
        cursor: "pointer",
        fontSize: "13px",
        fontWeight: 500,
        transition: "all 0.15s ease",
    });

    // ── Render ───────────────────────────────────────────────
    return (
        <div className="page-container">
            {/* Header */}
            <div className="page-header compact">
                <div className="page-title">
                    <BarChart3 size={24} />
                    <h1>Cork Utilisation Report</h1>
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                    <button className="download-btn" onClick={downloadXLSX} title="Export as XLSX">
                        <Download size={16} /> XLSX
                    </button>
                    <button className="download-btn" onClick={downloadCSV} title="Export as CSV">
                        <Download size={16} /> CSV
                    </button>
                </div>
            </div>

            {/* Date Presets */}
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
                <Calendar size={16} style={{ color: "var(--text-muted)" }} />
                {([
                    { key: "week" as DatePreset, label: "Past Week" },
                    { key: "30days" as DatePreset, label: "Last 30 Days" },
                    { key: "ytd" as DatePreset, label: "YTD" },
                    { key: "custom" as DatePreset, label: "Custom" },
                ]).map((p) => (
                    <button key={p.key} style={presetBtnStyle(p.key)} onClick={() => handlePresetChange(p.key)}>
                        {p.label}
                    </button>
                ))}
                {dateRange === "custom" && (
                    <>
                        <input
                            type="date"
                            className="search-input"
                            style={{ width: 150 }}
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                        />
                        <span style={{ color: "var(--text-muted)" }}>to</span>
                        <input
                            type="date"
                            className="search-input"
                            style={{ width: 150 }}
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                        />
                    </>
                )}
            </div>

            {/* Filters */}
            <div className="table-controls" style={{ marginBottom: 20 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <Filter size={16} style={{ color: "var(--text-muted)" }} />
                    <select
                        className="filter-select"
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                    >
                        <option value="all">All Statuses</option>
                        {statuses.map((s) => (
                            <option key={s} value={s}>{s}</option>
                        ))}
                    </select>
                </div>

                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <Filter size={16} style={{ color: "var(--text-muted)" }} />
                    <select
                        className="filter-select"
                        value={gradeFilter}
                        onChange={(e) => setGradeFilter(e.target.value)}
                    >
                        <option value="all">All Grades</option>
                        {grades.map((g) => (
                            <option key={g} value={g}>{g}</option>
                        ))}
                    </select>
                </div>

                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <Search size={16} style={{ color: "var(--text-muted)" }} />
                    <input
                        type="text"
                        className="search-input"
                        placeholder="Search by name, code, or grade..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                </div>
            </div>

            {/* Error */}
            {error && (
                <div style={{ padding: "12px 16px", marginBottom: 16, borderRadius: 8, background: "var(--status-error-bg, rgba(217,83,79,0.1))", color: "var(--status-error, #d9534f)", border: "1px solid var(--status-error, #d9534f)" }}>
                    {error}
                </div>
            )}

            {/* Summary Cards */}
            <div className="stats-grid" style={{ marginBottom: 24 }}>
                <div className="stat-card">
                    <div className="stat-icon blue">
                        <Users size={20} />
                    </div>
                    <div className="stat-content">
                        <div className="stat-value">{totals.headcount}</div>
                        <div className="stat-label">Total Staff</div>
                    </div>
                </div>
                <div className="stat-card">
                    <div className="stat-icon green">
                        <Users size={20} />
                    </div>
                    <div className="stat-content">
                        <div className="stat-value">{totals.activeCount}</div>
                        <div className="stat-label">Active Staff</div>
                    </div>
                </div>
                <div className="stat-card">
                    <div className="stat-icon purple">
                        <TrendingUp size={20} />
                    </div>
                    <div className="stat-content">
                        <div className="stat-value">{totals.chargeableHours.toFixed(1)}</div>
                        <div className="stat-label">Chargeable Hours</div>
                    </div>
                </div>
                <div className="stat-card">
                    <div className="stat-icon orange">
                        <Clock size={20} />
                    </div>
                    <div className="stat-content">
                        <div className="stat-value">{totals.nonChargeableHours.toFixed(1)}</div>
                        <div className="stat-label">Non-Chargeable</div>
                    </div>
                </div>
                <div className="stat-card highlight">
                    <div className="stat-icon yellow">
                        <Target size={20} />
                    </div>
                    <div className="stat-content">
                        <div className="stat-value">{totals.avgUtilisation.toFixed(1)}%</div>
                        <div className="stat-label">Avg Utilisation</div>
                    </div>
                </div>
                <div className="stat-card">
                    <div className="stat-icon blue">
                        <DollarSign size={20} />
                    </div>
                    <div className="stat-content">
                        <div className="stat-value">{formatCurrency(totals.totalRevenue)}</div>
                        <div className="stat-label">Total Revenue</div>
                    </div>
                </div>
            </div>

            {/* Main Data Table */}
            <div className="data-table-container">
                <DataTable
                    columns={columns}
                    data={filteredData}
                    customStyles={customStyles}
                    pagination
                    paginationPerPage={25}
                    paginationRowsPerPageOptions={[10, 25, 50]}
                    highlightOnHover
                    defaultSortFieldId={7}
                    defaultSortAsc={false}
                    expandableRows
                    expandableRowsComponent={ExpandedStaffDetail}
                    noDataComponent={
                        <div style={{ padding: 40, textAlign: "center", color: "var(--text-muted)" }}>
                            {data.length === 0
                                ? "Loading staff utilisation data..."
                                : "No results match the current filters."}
                        </div>
                    }
                />
            </div>
        </div>
    );
};

export default CorkUtilisation;
